
import { forwardRef } from "react";
import clsx from "clsx";

export type InputSize = "sm" | "md" | "lg";

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  inputSize?: InputSize;
  error?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const sizeClasses: Record<InputSize, string> = {
  sm: "input-sm",
  md: "",
  lg: "input-lg",
};

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      inputSize = "md",
      error = false,
      leftIcon,
      rightIcon,
      className,
      ...props
    },
    ref
  ) => {
    if (leftIcon || rightIcon) {
      // Determine positioning based on input size
      const iconLeftPos = inputSize === "lg" ? "1rem" : "0.75rem";
      const iconRightPos = inputSize === "lg" ? "1rem" : "0.75rem";
      const inputPaddingLeft = leftIcon ? (inputSize === "lg" ? "2.75rem" : "2.5rem") : undefined;
      const inputPaddingRight = rightIcon ? (inputSize === "lg" ? "2.75rem" : "2.5rem") : undefined;

      return (
        <div className="relative">
          {leftIcon && (
            <div
              className="absolute top-1/2 text-text-quiet pointer-events-none"
              style={{ left: iconLeftPos, transform: "translateY(-50%)" }}
            >
              {leftIcon}
            </div>
          )}
          <input
            ref={ref}
            className={clsx(
              "input",
              sizeClasses[inputSize],
              error && "input-error",
              className
            )}
            style={{
              paddingLeft: inputPaddingLeft,
              paddingRight: inputPaddingRight,
            }}
            {...props}
          />
          {rightIcon && (
            <div
              className="absolute top-1/2 text-text-quiet pointer-events-none"
              style={{ right: iconRightPos, transform: "translateY(-50%)" }}
            >
              {rightIcon}
            </div>
          )}
        </div>
      );
    }

    return (
      <input
        ref={ref}
        className={clsx(
          "input",
          sizeClasses[inputSize],
          error && "input-error",
          className
        )}
        {...props}
      />
    );
  }
);

Input.displayName = "Input";

export default Input;
